import { ComponentType, PropsWithChildren, SVGProps } from 'react';
import { cn } from '../../utils';
import { Tabs, TabsList, TabsTrigger } from '../Tabs';

export interface NavTab<T extends string = string> {
  value: T;
  label: string;
  Icon?: ComponentType<SVGProps<SVGSVGElement>>;
  disabled?: boolean;
}

export interface NavTabsProps<T extends string = string>
  extends PropsWithChildren {
  value: T;
  tabs: NavTab<T>[];
  onChange: (value: T) => void;
}

export const NavTabs = <T extends string>({
  value,
  tabs,
  onChange,
  children,
}: NavTabsProps<T>) => (
  <Tabs value={value} onValueChange={v => onChange(v as T)} className="w-full">
    <TabsList className="relative flex h-full w-full flex-wrap justify-start gap-x-2.5 gap-y-2 bg-transparent p-0 pb-4">
      {tabs.map(({ value, label, Icon, disabled }) => (
        <TabsTrigger
          key={value}
          value={value}
          disabled={disabled}
          className={cn(
            'text-muted-foreground relative cursor-pointer rounded-none px-0 pt-2 pr-2 pb-2 pl-2 text-sm',
            'data-[state=active]:text-primary data-[state=active]:font-semibold data-[state=active]:shadow-none',
            'data-[state=active]:after:absolute',
            'data-[state=active]:after:right-0 data-[state=active]:after:left-0',
            'data-[state=active]:after:-bottom-0.5',
            'data-[state=active]:after:h-0.75',
            'data-[state=active]:after:w-full',
            'data-[state=active]:after:rounded-full',
            'data-[state=active]:after:bg-secondary'
          )}
          aria-label={label}
        >
          <span className="flex items-center gap-2">
            {Icon && <Icon className="size-4" />}
            {label}
          </span>
        </TabsTrigger>
      ))}
    </TabsList>
    {children}
  </Tabs>
);
